/**
 * frontend/src/sdk/hooks/use-search.ts
 *
 * Typed TanStack Query hooks for news, sentiment, and search.
 */

import { useMutation, useQuery } from "@tanstack/react-query";

import { apiGet, apiPost } from "@/lib/api-client";
import type { NewsArticle, SearchResult, SentimentResult, TrendData } from "@/sdk/types/search";

// =============================================================================
// NEWS
// =============================================================================

export function useNewsSearch(query: string, options?: { limit?: number; enabled?: boolean }) {
  // Backend: POST /api/news/search (no GET search endpoint).
  // Implemented as a query that fires a POST via the mutation wrapper underneath.
  // Uses the mutation-based helper useNewsSearchMutation for fire-on-demand;
  // this hook is preserved for backward-compat but delegates to the POST path.
  return useQuery({
    queryKey: ["search", "news", query, options?.limit],
    queryFn: () =>
      apiPost<{ articles: NewsArticle[]; total: number }>(
        "/api/news/search",
        { query, limit: options?.limit ?? 20 },
      ),
    enabled: (options?.enabled ?? true) && Boolean(query),
    staleTime: 60_000,
  });
}

export function useLatestNews(options?: { limit?: number; market_id?: string }) {
  const params = new URLSearchParams();
  if (options?.limit) params.set("limit", String(options.limit));
  if (options?.market_id) params.set("market_id", options.market_id);

  return useQuery({
    queryKey: ["search", "news", "latest", options],
    queryFn: () =>
      apiGet<{ articles: NewsArticle[]; total: number }>(`/api/news/latest?${params}`),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

export function useNewsSearchMutation() {
  return useMutation({
    mutationFn: (params: { query: string; limit?: number }) =>
      apiPost<{ success: boolean; articles: NewsArticle[] }>(
        "/api/news/search",
        params as Record<string, unknown>,
      ),
  });
}

// =============================================================================
// SENTIMENT
// =============================================================================

export function useMarketSentiment(marketId: string, enabled = true) {
  return useQuery({
    queryKey: ["search", "sentiment", marketId],
    queryFn: () =>
      apiGet<SentimentResult>(`/api/comments/sentiment/${marketId}`),
    enabled: enabled && Boolean(marketId),
    staleTime: 120_000,
  });
}

// =============================================================================
// GOOGLE TRENDS
// =============================================================================

export function useGoogleTrends(keyword: string, enabled = true) {
  // Backend: GET /api/data/trends/interest?keywords={kw1,kw2} (free_data router,
  //          prefix /api/data). The param is the REQUIRED plural "keywords"
  //          (comma-separated, max 5) — the singular "keyword" 422s.
  //          /api/external/trends does not exist.
  return useQuery({
    queryKey: ["search", "trends", keyword],
    queryFn: () =>
      apiGet<TrendData>(
        `/api/data/trends/interest?keywords=${encodeURIComponent(keyword)}`,
      ),
    enabled: enabled && Boolean(keyword),
    staleTime: 300_000,
  });
}

// =============================================================================
// TWITTER
// =============================================================================

export function useTwitterSearch(query: string, options?: { limit?: number; enabled?: boolean }) {
  // Bug #15 (audit 2026-05-06): the queryKey previously omitted ``limit``
  // while the URL embedded ``count=${limit}``. Two calls with different
  // limits collided in cache, so a later 50-item request returned the
  // earlier 20-item cached result.
  const limit = options?.limit ?? 20;
  return useQuery({
    queryKey: ["search", "twitter", query, limit],
    queryFn: () =>
      apiGet<{ tweets: SearchResult[]; total: number }>(
        `/api/twitter/search?query=${encodeURIComponent(query)}&count=${limit}`,
      ),
    enabled: (options?.enabled ?? false) && Boolean(query),
    staleTime: 60_000,
  });
}

export function useTwitterSearchMutation() {
  return useMutation({
    mutationFn: (params: { query: string; limit?: number }) =>
      apiGet<{ tweets: SearchResult[]; total: number }>(
        `/api/twitter/search?query=${encodeURIComponent(params.query)}&count=${params.limit ?? 20}`,
      ),
  });
}
